import clsx from "clsx";
import { useMemo } from "react";

import { useUserContext } from "@/features/auth/contexts/useUserContext";
import { Button } from "@/features/common/components/Button";
import { Rating } from "@/features/common/components/Rating";
import { Spinner } from "@/features/common/components/Spinner";
import {
  DialogViewEnum,
  useDialogContext,
} from "@/features/common/contexts/useDialogContext";
import { useModelRatingSummaryQuery } from "@/generated/graphql-hooks";

export const RatingsCard = ({ modelUserId }: { modelUserId: string }) => {
  const { user } = useUserContext();
  const { openDialog } = useDialogContext();
  const { data: { modelRatingsScore, modelRatingsCount } = {}, isLoading } =
    useModelRatingSummaryQuery({ modelUserId });
  const isCurrentUser = user?.id === modelUserId;

  const ratingItems = useMemo(
    () => [
      {
        label: "評分",
        content: (
          <div className="py-1/2">
            <Rating
              disabled
              value={modelRatingsScore}
              onClick={null}
              size="sm"
            />
          </div>
        ),
      },
      {
        label: "商戶評分",
        content: modelRatingsCount > 0 ? `${modelRatingsCount}個` : "-",
      },
    ],
    [modelRatingsScore, modelRatingsCount],
  );

  return (
    <div className="w-full justify-between space-y-5 rounded-xl bg-white py-3 shadow sm:flex sm:space-y-0">
      <div
        className={clsx(
          "grid flex-1 grid-cols-2 items-center text-sm font-light text-gray-700",
          isCurrentUser && "lg:grid-cols-3",
        )}
      >
        {ratingItems.map(({ label, content }, index) => (
          <div
            className={clsx(
              index !== ratingItems.length - 1
                ? "border-r"
                : isCurrentUser && "sm:border-r",
              "inline-flex flex-col items-center space-y-1 border-gray-100",
            )}
            key={index}
          >
            <div>{label}</div>
            <div className="flex items-center text-base font-normal text-gray-900">
              {!!isLoading && <Spinner className="!text-primary" />}
              {!isLoading && content}
            </div>
          </div>
        ))}
        {isCurrentUser && (
          <div className="col-span-full mt-3 flex items-center justify-center px-3 lg:col-span-1 lg:mt-0 lg:px-0">
            <Button
              className="py-2 px-4 lg:w-auto"
              variant="outline"
              size="sm"
              onClick={() =>
                openDialog({
                  view: DialogViewEnum.ModelRatingListDialog,
                  modelUserId,
                })
              }
            >
              查看所有評分
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
